import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type {
  PositioningClient,
  PositioningIndicatorDto,
  PositioningResponseDto,
  RateSeniorityLevelDto,
} from '@/api/positioning-client';
import { usePositioningClient } from '@/api/positioning-client-context';

// =============================================================================
// use-positioning.ts — Hooks TanStack Query de l'indicateur de positionnement F-10 (S17).
// =============================================================================
// Une LECTURE (indicateurs unaires : drapeau orange/masqué) en useQuery — aucune mutation (le
// fournisseur LIT, jamais d'écriture). Le client effectif vient du PositioningClientProvider
// (doublable, stub-first). ZÉRO règle métier ici (G0-S17-16) : le verdict orange (tjm ≥ P75) ET le
// masquage d'anonymat sont l'autorité de l'API. La ressource est la CAMPAGNE (route GELÉE D1).
//
// MODÈLE PER-COUPLE (S17 réconciliation, option B) : l'API verdicte UN couple contre le `tjmValueEur`
// SAUVEGARDÉ de ce couple. Le front émet donc :
//   • `usePositioningActivation(campaignId, cycleNumber, enabled)` : sonde SANS tjm — sert
//     l'activation (`indicators.length > 0`) et la liste des couples masqués/présents du marché ;
//   • `usePositioningCouple(campaignId, cycleNumber, {profile, level, tjmValueEur}, enabled)` :
//     verdicte UN couple ; keyé sur `[positioning, campaignId, profile, level, tjm]`. ENF-01 :
//     la query ne re-fire QUE sur valeur SAUVEGARDÉE (post-autosave), JAMAIS par keystroke ;
//     TanStack dédoublonne par clé (un couple = une query partagée).

const POSITIONING_KEY = ['positioning'] as const;

/**
 * Clé de query d'un COUPLE F-10 (per-couple). `tjm` dans la clé ⇒ une nouvelle valeur sauvegardée
 * = une nouvelle entrée de cache ; aucune valeur sauvegardée identique ne re-fetche (dédup ENF-01).
 * `tjm === undefined` ⇒ clé de la SONDE d'activation (sans verdict).
 */
export function positioningKey(
  campaignId: string,
  cycleNumber: number,
  profileCode?: string,
  seniorityLevel?: RateSeniorityLevelDto,
  tjmValueEur?: number,
): readonly unknown[] {
  return [
    ...POSITIONING_KEY,
    campaignId,
    cycleNumber,
    profileCode ?? null,
    seniorityLevel ?? null,
    tjmValueEur ?? null,
  ];
}

/**
 * SONDE D'ACTIVATION : lit les couples du marché de la campagne (SANS tjm ⇒ aucun verdict orange).
 * Sert à dériver `positioningActive = indicators.length > 0` (l'API ne renvoie d'indicateurs que si
 * des soumissions Cycle 1 alimentent le marché). `enabled` = Cycle 2 (fenêtre d'affichage F-10).
 */
export function usePositioningActivation(
  campaignId: string | undefined,
  cycleNumber: number,
  enabled: boolean,
): UseQueryResult<PositioningResponseDto, Error> {
  const client: PositioningClient = usePositioningClient();
  return useQuery<PositioningResponseDto, Error>({
    queryKey: positioningKey(campaignId ?? '', cycleNumber),
    queryFn: () => client.getPositioning(campaignId ?? '', { cycleNumber }),
    enabled: enabled && campaignId !== undefined,
  });
}

/** Identité + valeur SAUVEGARDÉE d'un couple à verdicter (per-couple). */
export interface PositioningCoupleInput {
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  /** TJM COMMITTÉ (autosave), PAS la frappe : ENF-01 (la query ne fire que sur valeur sauvegardée). */
  readonly tjmValueEur: number;
}

/**
 * Verdicte UN couple (profil×niveau) contre son `tjmValueEur` SAUVEGARDÉ. Renvoie l'indicateur
 * unaire `{ masked, orange }` du couple, ou `undefined` (couple inconnu du marché). Keyé sur
 * `[positioning, campaignId, profile, level, tjm]` : déclenché après autosave (valeur committée),
 * dédoublonné, ZÉRO appel par frappe (ENF-01). `enabled=false` (pas de valeur sauvegardée) ⇒ aucune
 * requête. Le front ne dérive RIEN : il AFFICHE le verdict serveur (G0-S17-16).
 */
export function usePositioningCouple(
  campaignId: string | undefined,
  cycleNumber: number,
  couple: PositioningCoupleInput | undefined,
  enabled: boolean,
): UseQueryResult<PositioningIndicatorDto | undefined, Error> {
  const client: PositioningClient = usePositioningClient();
  return useQuery<PositioningIndicatorDto | undefined, Error>({
    queryKey: positioningKey(
      campaignId ?? '',
      cycleNumber,
      couple?.profileCode,
      couple?.seniorityLevel,
      couple?.tjmValueEur,
    ),
    queryFn: async () => {
      const response = await client.getPositioning(campaignId ?? '', {
        cycleNumber,
        profileCode: couple?.profileCode,
        seniorityLevel: couple?.seniorityLevel,
        tjmValueEur: couple?.tjmValueEur,
      });
      // L'API filtre déjà par couple : l'unique indicateur (ou aucun) est le verdict de CE couple.
      return response.indicators[0];
    },
    enabled: enabled && campaignId !== undefined && couple !== undefined,
  });
}
